import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiSearch, FiMessageCircle, FiThumbsUp, FiClock, FiUser } = FiIcons;

const Questions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showQuestionForm, setShowQuestionForm] = useState(false);

  const categories = [
    { id: 'all', label: 'All Questions' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'sports-marketing', label: 'Sports Marketing' },
    { id: 'digital', label: 'Digital Marketing' },
    { id: 'strategy', label: 'Strategy' }
  ];

  const questions = [
    {
      id: 1,
      title: 'What are the key differences between B2B and B2C marketing strategies?',
      category: 'marketing',
      author: 'Sarah M.',
      time: '2 hours ago',
      answers: 5,
      likes: 12,
      preview: 'I\'m struggling to understand the fundamental differences in approach...',
      hasInstructorReply: true
    },
    {
      id: 2,
      title: 'How do sports sponsorship deals impact brand awareness?',
      category: 'sports-marketing',
      author: 'Mike R.',
      time: '5 hours ago',
      answers: 8,
      likes: 18,
      preview: 'Looking for real-world examples of successful sports sponsorships...',
      hasInstructorReply: true
    },
    {
      id: 3,
      title: 'Best practices for social media marketing in 2024?',
      category: 'digital',
      author: 'Emma L.',
      time: '1 day ago',
      answers: 12,
      likes: 25,
      preview: 'What are the latest trends and strategies for social media marketing...',
      hasInstructorReply: false
    },
    {
      id: 4,
      title: 'How to measure ROI in sports marketing campaigns?',
      category: 'sports-marketing',
      author: 'David K.',
      time: '2 days ago',
      answers: 6,
      likes: 14,
      preview: 'Need help understanding metrics and KPIs for sports marketing...',
      hasInstructorReply: true
    }
  ];

  const filteredQuestions = questions.filter(question => {
    const matchesSearch = question.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         question.preview.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || question.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <div className="flex items-center justify-center mb-6">
          <img 
            src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751577427758-blob" 
            alt="Professor Klein" 
            className="w-16 h-16 rounded-full border-2 border-blue-600 mr-4 object-cover"
          />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Q&A Forum</h1>
            <p className="text-gray-600">Ask Professor Klein and your peers questions</p>
          </div>
        </div>
      </motion.div>

      {/* Search and Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="bg-white rounded-lg shadow-md p-6 mb-8"
      >
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowQuestionForm(!showQuestionForm)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} />
            <span>Ask Question</span>
          </button>
        </div>
      </motion.div>

      {/* Question Form */}
      {showQuestionForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-lg shadow-md p-6 mb-8"
        >
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Ask Professor Klein a Question</h3>
          <form className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Question Title</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="What's your question?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="marketing">Marketing</option>
                <option value="sports-marketing">Sports Marketing</option>
                <option value="digital">Digital Marketing</option>
                <option value="strategy">Strategy</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Question Details</label>
              <textarea
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Provide more details about your question..."
              ></textarea>
            </div>
            <div className="flex gap-4">
              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Post Question
              </button>
              <button
                type="button"
                onClick={() => setShowQuestionForm(false)}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Questions List */}
      <div className="space-y-4">
        {filteredQuestions.map((question, index) => (
          <motion.div
            key={question.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <h3 className="text-xl font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                    {question.title}
                  </h3>
                  {question.hasInstructorReply && (
                    <span className="ml-2 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                      Professor Klein Replied
                    </span>
                  )}
                </div>
                <p className="text-gray-600 mb-3">{question.preview}</p>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiUser} />
                    <span>{question.author}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiClock} />
                    <span>{question.time}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <SafeIcon icon={FiMessageCircle} />
                  <span>{question.answers}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <SafeIcon icon={FiThumbsUp} />
                  <span>{question.likes}</span>
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                question.category === 'marketing' ? 'bg-blue-100 text-blue-800' :
                question.category === 'sports-marketing' ? 'bg-green-100 text-green-800' :
                question.category === 'digital' ? 'bg-purple-100 text-purple-800' :
                'bg-orange-100 text-orange-800'
              }`}>
                {categories.find(cat => cat.id === question.category)?.label}
              </span>
              <button className="text-blue-600 hover:text-blue-700 font-medium">
                View Details
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredQuestions.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center py-12"
        >
          <SafeIcon icon={FiSearch} className="text-gray-400 text-4xl mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No questions found</h3>
          <p className="text-gray-600">Try adjusting your search terms or category filter</p>
        </motion.div>
      )}
    </div>
  );
};

export default Questions;